import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { formatClassTimeDisplay } from '../utils/classTimeUtils';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function normalizeBatchesResponse(data) {
  const list = Array.isArray(data)
    ? data
    : data?.batches
    ? (Array.isArray(data.batches) ? data.batches : data.batches.results || [])
    : data?.results || [];

  if (!Array.isArray(list)) return [];

  return list.map((b, idx) => {
    const id = b?.batch_id ?? b?.id ?? idx;
    const batchName = b?.batch_name ?? b?.name ?? '';
    const batchType = b?.batch_type ?? b?.type ?? '';
    // Keep IDs as strings (UUID-safe); never coerce to Number.
    const courseIdRaw = b?.course?.course_id ?? b?.course?.id ?? b?.course ?? b?.course_id;
    const courseId = courseIdRaw != null ? String(courseIdRaw) : '';
    const teacherIdRaw =
      b?.teacher?.teacher_id ?? b?.teacher?.id ?? b?.teacher ?? b?.teacher_id;
    const teacherId = teacherIdRaw != null ? String(teacherIdRaw) : '';
    const courseName = b?.course_name ?? b?.course?.course_name ?? b?.course?.name ?? '';
    const teacherName = b?.teacher_name ?? b?.teacher?.full_name ?? b?.teacher?.name ?? '';
    const startDate = b?.start_date ?? b?.startDate ?? '';
    const classTime = b?.class_time ?? b?.startTime ?? '';
    const capacity = b?.capacity ?? 0;
    const status = b?.status ?? '';
    const students = b?.student ?? '';

    return {
      id,
      batchName: String(batchName),
      batchType: String(batchType),
      courseId,
      courseName: String(courseName),
      teacherId,
      teacherName: String(teacherName),
      startDate: String(startDate),
      classTime: String(classTime),
      capacity: Number(capacity) || 0,
      status: String(status),
      students: String(students),
    };
  });
}

function Batches() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const requestIdRef = useRef(0);

  // Create lookup maps for course and teacher names dynamically
  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach((course) => {
      const idRaw = course.course_id || course.id;
      const id = idRaw != null ? String(idRaw) : '';
      const name = course.course_name || course.name || '';
      if (id && name) {
        map[id] = name;
      }
    });
    return map;
  }, [courses]);

  const teacherMap = useMemo(() => {
    const map = {};
    teachers.forEach((teacher) => {
      const idRaw = teacher.teacher_id || teacher.id;
      const id = idRaw != null ? String(idRaw) : '';
      const name = teacher.full_name || teacher.name || '';
      if (id && name) {
        map[id] = name;
      }
    });
    return map;
  }, [teachers]);

  // Enrich batches with course and teacher names dynamically
  const enrichedBatches = useMemo(() => {
    return batches.map((batch) => {
      const courseName = batch.courseName || courseMap[batch.courseId] || '';
      const teacherName = batch.teacherName || teacherMap[batch.teacherId] || '';

      return {
        ...batch,
        courseName,
        teacherName,
      };
    });
  }, [batches, courseMap, teacherMap]);

  const loadData = useCallback(
    async ({ isRefresh = false } = {}) => {
      const requestId = Date.now();
      requestIdRef.current = requestId;
      if (!isRefresh) {
        setLoading(true);
      }
      setError('');
      try {
        const [batchesRes, coursesRes, teachersRes] = await Promise.all([
          apiClient.get('/api/batches/'),
          apiClient.get('/api/courses/'),
          apiClient.get('/api/auth/teachers/'),
        ]);
        if (requestIdRef.current !== requestId) return;

        setBatches(normalizeBatchesResponse(batchesRes.data));

        const coursesList = Array.isArray(coursesRes.data)
          ? coursesRes.data
          : coursesRes.data?.courses?.results ||
            coursesRes.data?.courses ||
            coursesRes.data?.results ||
            [];
        setCourses(coursesList);

        const teachersList = Array.isArray(teachersRes.data)
          ? teachersRes.data
          : teachersRes.data?.teachers?.results ||
            teachersRes.data?.teachers ||
            teachersRes.data?.results ||
            [];
        setTeachers(teachersList);
      } catch (e) {
        if (requestIdRef.current !== requestId) return;
        if (e?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            'Unable to load batches. Please try again.'
        );
      } finally {
        if (requestIdRef.current !== requestId) return;
        setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadData({ isRefresh: true });
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, [loadData]);

  const handleEdit = (batchId) => {
    navigate(`/batches/edit/${batchId}`);
  };

  const handleDelete = async (batchId, batchName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${batchName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(batchId);
    try {
      await apiClient.delete(`/api/batches/${batchId}/`);
      // Remove batch from list
      setBatches((prev) => prev.filter((b) => b.id !== batchId));
      setError('');
    } catch (err) {
      console.error('Error deleting batch:', err);
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Unable to delete batch. Please try again.';
      setError(errorMsg);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredBatches = useMemo(() => {
    const term = search.toLowerCase().trim();
    return enrichedBatches.filter((batch) => {
    const matchesSearch =
      !term ||
        batch.batchName.toLowerCase().includes(term) ||
        batch.teacherName.toLowerCase().includes(term);

      const matchesType =
        typeFilter === 'All' ||
        batch.batchType.toLowerCase() === typeFilter.toLowerCase();
      const matchesStatus =
        statusFilter === 'All' ||
        batch.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesType && matchesStatus;
  });
  }, [enrichedBatches, search, typeFilter, statusFilter]);

  return (
    <PageTemplate
      title="Batch Management"
      subtitle="Organize batches, schedules, and capacities for your courses"
    >
      <div className="batches-page">
        <div className="batch-summary">
          <div className="batch-summary-card">
            <span className="batch-summary-label">Total Batches</span>
            <span className="batch-summary-value">{batches.length}</span>
          </div>
          <div className="batch-summary-card">
            <span className="batch-summary-label">Weekday Batches</span>
            <span className="batch-summary-value">
              {batches.filter((b) => b.batchType.toLowerCase() === 'weekdays').length}
            </span>
          </div>
          <div className="batch-summary-card">
            <span className="batch-summary-label">Weekend Batches</span>
            <span className="batch-summary-value">
              {batches.filter((b) => b.batchType.toLowerCase() === 'weekend').length}
            </span>
          </div>
          <div className="batch-summary-card">
            <span className="batch-summary-label">Active Capacity</span>
            <span className="batch-summary-value">
              {batches
                .filter((b) => b.status.toLowerCase() === 'active')
                .reduce((sum, b) => sum + (b.students ? b.students.split(',').length : 0), 0)}
              /
              {batches
                .filter((b) => b.status.toLowerCase() === 'active')
                .reduce((sum, b) => sum + b.capacity, 0)}
            </span>
          </div>
        </div>

        {error ? (
          <p className="form-error" style={{ marginTop: '12px' }}>
            {error}
          </p>
        ) : null}

        <div className="batch-filters">
          <input
            type="search"
            placeholder="Search by batch or teacher..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="select-input"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="All">All Types</option>
            <option value="weekdays">Weekdays</option>
            <option value="weekend">Weekend</option>
          </select>
          <select
            className="select-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/batches/add')}
          >
            Add Batch
          </button>
        </div>

        <div className="teachers-table-wrapper" role="region" aria-label="Batches table">
          <table className="teachers-table">
            <thead>
              <tr>
                <th>Batch Name</th>
                <th>Type</th>
                <th>Teacher</th>
                <th>Class Time</th>
                <th className="table-th-center">Status</th>
                <th className="actions-cell table-th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="teachers-empty teachers-empty--loading">
                    Loading batches…
                  </td>
                </tr>
              ) : filteredBatches.length === 0 ? (
                <tr>
                  <td colSpan="6" className="teachers-empty">
                    No batches match your filters.
                  </td>
                </tr>
              ) : (
                filteredBatches.map((batch) => (
                  <tr key={batch.id}>
                    <td className="table-cell-strong">{batch.batchName}</td>
                    <td>
                      <span className="batch-type-pill">
                        {batch.batchType === 'weekdays' ? 'Weekdays' : 'Weekend'}
                      </span>
                    </td>
                    <td>
                      {batch.teacherName ? (
                        <span className="table-cell-strong">{batch.teacherName}</span>
                      ) : batch.teacherId ? (
                        <span className="table-cell-muted">Resolving… ({String(batch.teacherId).slice(0, 8)}…)</span>
                      ) : (
                        <span className="table-cell-muted">—</span>
                      )}
                    </td>
                    <td className="table-deadline">
                      {formatClassTimeDisplay(batch.classTime)}
                    </td>
                    <td className="table-td-center">
                      <span
                        className={`batch-status-pill batch-status-${batch.status.toLowerCase()}`}
                      >
                        {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                      </span>
                    </td>
                    <td className="actions-cell table-td-center">
                      <div className="table-actions">
                        <button
                          onClick={() => handleEdit(batch.id)}
                          type="button"
                          className="table-btn table-btn--primary"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(batch.id, batch.batchName)}
                          type="button"
                          disabled={deletingId === batch.id}
                          className="table-btn table-btn--danger"
                        >
                          {deletingId === batch.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageTemplate>
  );
}

export default Batches;
